import React, { useState } from 'react';
import { MenuProps } from '@mui/material';
import { Menu, MenuItem } from '~/UIKit';
import { Trans, useTranslation } from 'react-i18next';
import { ManageBookingsConfirmationModal } from './ManageBookingsConfirmationModal';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_INBETWEEN } from '~/Constants/UI.constants';
import { useRequestPendingBooking } from '~/requests/coreService/useRequestPendingBookings';
import { PendingBookingResponse } from '~/requests/coreService/useRequestPendingBookingsSettings';
import { ToastVariant, useHasAccess, useToastMessage } from '~/Hooks';
import { Segment } from '~/Helpers/Segment.helper';
import { SegmentTrackName } from '~/Constants/Segment.constants';

export type IManageBookingsMenu = Omit<MenuProps, 'open'> & {
  handleCloseMenu: () => void;
  isLoading: boolean;
  pendingBookingsSettings: PendingBookingResponse;
};

enum SELECTED_ITEMS {
  confirmationModal = 1,
  noShow,
  lateCancellation,
}

const MODAL_TYPE_MAPPING: Record<number, string> = {
  [SELECTED_ITEMS.confirmationModal]: 'CheckIn',
  [SELECTED_ITEMS.noShow]: 'NoShow',
  [SELECTED_ITEMS.lateCancellation]: 'LateCancellation',
};

export const useManageBookingsStyles = makeStyles(() => ({
  menu: {
    marginTop: 8,
    '& .MuiPaper-root': {
      padding: '8px 0',
    },
  },
  item: {
    padding: '8px 16px',
    fontSize: FONT_SIZE_INBETWEEN,
  },
  textWrapper: {
    display: 'block',
  },
}));

export const ManageBookingsMenu = ({
  anchorEl,
  handleCloseMenu,
  isLoading,
  pendingBookingsSettings,
}: IManageBookingsMenu) => {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState(0);
  const { classes } = useManageBookingsStyles();
  const [emailOrMemberId, setEmailOrMemberId] = useState('');
  const { hasSuperAdminAccess } = useHasAccess();
  const [eventId, setEventId] = useState<number>(0);
  const { mutate } = useRequestPendingBooking();
  const { showToast } = useToastMessage();

  const handleMenuItemClick = (index: number) => {
    setSelectedItem(index);
    Segment.track(SegmentTrackName.MANUAL_BOOKING_SELECTED, {
      bookingType: MODAL_TYPE_MAPPING[index],
    });
    handleCloseMenu();
  };

  const handleSubmit = () => {
    const payload = {
      eventId,
      emailOrMemberId,
      requestedEndState: MODAL_TYPE_MAPPING[selectedItem],
    };

    Segment.track(SegmentTrackName.MANUAL_BOOKING_SUBMIT, {
      bookingType: payload.requestedEndState,
    });

    mutate(payload);
    setSelectedItem(0);

    showToast(
      `NewManualBooking_${emailOrMemberId}`,
      <div className={classes.textWrapper}>
        <Trans
          i18nKey="app.checkin.pendingBookings.toast"
          values={{ emailOrMemberId }}
          components={{ b: <b />, break: <br /> }}
        />
      </div>,
      ToastVariant.WARNING,
      true,
      undefined,
      7000,
    );
  };

  const openCorrectModal = () => {
    switch (selectedItem) {
      case SELECTED_ITEMS.confirmationModal:
        return (
          <ManageBookingsConfirmationModal
            open
            onClose={() => setSelectedItem(0)}
            onConfirm={handleSubmit}
            closeMenu={handleCloseMenu}
            emailOrMemberId={emailOrMemberId}
            setEmailOrMemberId={setEmailOrMemberId}
            eventId={eventId}
            setEventId={setEventId}
            title={t('app.events.manualCheckin.checkinModal.title')}
            subtitle={t('app.events.manualCheckin.checkinModal.subtitle')}
            note={t('app.events.manualCheckin.checkinModal.note')}
          />
        );
      case SELECTED_ITEMS.noShow:
        return (
          <ManageBookingsConfirmationModal
            open
            onClose={() => setSelectedItem(0)}
            onConfirm={handleSubmit}
            closeMenu={handleCloseMenu}
            emailOrMemberId={emailOrMemberId}
            setEmailOrMemberId={setEmailOrMemberId}
            eventId={eventId}
            setEventId={setEventId}
            title={t('app.events.manualCheckin.noShowModal.title')}
            subtitle={t('app.events.manualCheckin.noShowModal.subtitle')}
            note={t('app.events.manualCheckin.noShowModal.note')}
          />
        );
      case SELECTED_ITEMS.lateCancellation:
        return (
          <ManageBookingsConfirmationModal
            open
            onClose={() => setSelectedItem(0)}
            onConfirm={handleSubmit}
            closeMenu={handleCloseMenu}
            emailOrMemberId={emailOrMemberId}
            setEmailOrMemberId={setEmailOrMemberId}
            eventId={eventId}
            setEventId={setEventId}
            title={t('app.events.manualCheckin.lateCancellationModal.title')}
            subtitle={t('app.events.manualCheckin.lateCancellationModal.subtitle')}
            note={t('app.events.manualCheckin.lateCancellationModal.note')}
          />
        );
      default:
        return false;
    }
  };

  return (
    <div data-testid="manage-bookings">
      <Menu
        anchorEl={anchorEl}
        className={classes.menu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        keepMounted
        onClose={handleCloseMenu}
        open={Boolean(anchorEl)}
      >
        {(hasSuperAdminAccess ||
          (!isLoading && pendingBookingsSettings?.manualCheckInIsAllowed)) && (
          <MenuItem
            className={classes.item}
            onClick={() => handleMenuItemClick(SELECTED_ITEMS.confirmationModal)}
          >
            {t('app.events.manualCheckin.checkin')}
          </MenuItem>
        )}
        {(hasSuperAdminAccess ||
          (!isLoading && pendingBookingsSettings?.manualNoShowIsAllowed)) && (
          <MenuItem
            className={classes.item}
            onClick={() => handleMenuItemClick(SELECTED_ITEMS.noShow)}
          >
            {t('app.events.manualCheckin.noShow')}
          </MenuItem>
        )}
        {(hasSuperAdminAccess ||
          (!isLoading && pendingBookingsSettings?.manualLateCancellationIsAllowed)) && (
          <MenuItem
            className={classes.item}
            onClick={() => handleMenuItemClick(SELECTED_ITEMS.lateCancellation)}
          >
            {t('app.events.manualCheckin.lateCancellation')}
          </MenuItem>
        )}
      </Menu>
      {openCorrectModal()}
    </div>
  );
};
